import React from "react";
import Link from "next/link";
import {
  INewAppointmentArgs,
  IPathState,
  IAppointmentRouteState,
  FIRST_TIME_CLIENT,
  RESCHEDULE_MAIN,
} from ".";
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Space,
  Typography,
  message,
} from "antd";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { extractErrorMessage } from "@/lib/helpers";
import { formatMoney, formatServicePrice } from "@/lib/helpers/servicePricing";
import type { AppointmentForm } from "@/lib/hooks/useAppointment";
import { useBookingApi } from "./bookingApi";
import { resolveBookingCustomerName } from "./bookingCustomerName";
import { useRepricedServices } from "./useRepricedServices";
import ServicePricingStatus from "./components/ServicePricingStatus";
import {
  collectionAgentNotice,
  paymentPolicySentence,
} from "./paymentPolicyCopy";
import { BUSINESSES, DEFAULT_EDITION_BUSINESS } from "@/consts/businesses";
import { resolveBusinessForAppointment } from "@/lib/api/openingsLocations";

const { Text } = Typography;

type AppointmentService = AppointmentForm["services"][number];

const round2 = (value: number) => Math.round(value * 100) / 100;

const formatServiceNames = (names: string[]) => {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

const getServiceBasePrice = (service: AppointmentService) =>
  service.option?.price ?? service.price;

const getServiceRequirement = (
  service: AppointmentService,
): "NONE" | "DEPOSIT" | "FULL" =>
  service.paymentRequirement ?? (service.hasPayAdvance ? "FULL" : "NONE");

// Mirror the API's per-service charge math (packages/database/src/payments.ts)
// so the amount we show matches what will actually be charged.
const getServiceChargeNow = (service: AppointmentService) => {
  const basePrice = getServiceBasePrice(service);
  const requirement = getServiceRequirement(service);
  if (requirement === "FULL") return basePrice;
  if (requirement === "DEPOSIT") {
    const deposit =
      service.depositAmount && service.depositAmount > 0
        ? service.depositAmount
        : basePrice;
    return Math.min(deposit, basePrice);
  }
  return 0;
};

interface IProps {
  appointment: AppointmentForm;
  routeState: IAppointmentRouteState;
  onRouteChange: (path: IPathState, state: any) => void;
  onCreate: (args: INewAppointmentArgs) => void;
}
const SetupPayment = ({
  appointment,
  routeState,
  onRouteChange,
  onCreate,
}: IProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const bookingApi = useBookingApi();
  const [isSubmitting, setSubmitting] = React.useState(false);
  // Remembers the payment method created by handleAddCard so Back can carry
  // it to FirstClient if the appointment create that follows fails. Without
  // it, resubmitting identity would reroute here again (FirstClient reroutes
  // whenever isCreditCardRequired && !existingPaymentMethodId) and the
  // customer would enter a card — and get a CRM write — a second time.
  const [createdPaymentMethodId, setCreatedPaymentMethodId] =
    React.useState<string>();
  const [messageApi, contextHolder] = message.useMessage();
  const { upcomingAppointments, phoneNumber, client } = routeState;
  const customerName = resolveBookingCustomerName(routeState);
  const [agreed, setAgreed] = React.useState(false);

  // A per-customer deposit flag (set by the business on the customer's
  // account) forces full prepayment regardless of each service's own payment
  // requirement — mirrors the Openings booking page customerDepositRequired
  // behavior and matches what the server actually charges.
  const customerDepositRequired = Boolean(client?.depositRequired);

  // One authoritative result is shared with review and phone routing. Never
  // render a charge, card field or submit action from canonical fallback data.
  const pricing = useRepricedServices();
  if (pricing.status !== "ready") {
    return (
      <>
        {contextHolder}
        <ServicePricingStatus />
      </>
    );
  }
  const services = pricing.services;
  const totalPrice = round2(
    services.reduce((sum, service) => sum + getServiceBasePrice(service), 0),
  );
  const serviceChargeNow = round2(
    services.reduce((sum, service) => sum + getServiceChargeNow(service), 0),
  );
  const chargeNow = customerDepositRequired ? totalPrice : serviceChargeNow;
  const dueLater = round2(Math.max(0, totalPrice - chargeNow));
  const isFullCharge = dueLater <= 0;
  const chargeLabel = `Charge ${formatMoney(chargeNow)}`;

  // Bucket services by payment requirement so we can describe a MIXED cart
  // (some services fully prepaid, others deposit-only) accurately.
  const paymentBuckets = services.reduce(
    (acc, service) => {
      const requirement = getServiceRequirement(service);
      if (requirement === "FULL") {
        acc.fullTotal += getServiceBasePrice(service);
        acc.fullNames.push(service.title);
      } else if (requirement === "DEPOSIT") {
        acc.depositTotal += getServiceChargeNow(service);
        acc.depositNames.push(service.title);
      }
      return acc;
    },
    {
      fullTotal: 0,
      depositTotal: 0,
      fullNames: [] as string[],
      depositNames: [] as string[],
    },
  );
  const isMixed =
    paymentBuckets.fullTotal > 0 && paymentBuckets.depositTotal > 0;

  const chargeNowLabel = isFullCharge
    ? "Total due now"
    : isMixed
      ? "Due now"
      : "Deposit due now";

  const paymentMessage = customerDepositRequired
    ? `Full prepayment is required to confirm bookings for this account. Your card will be charged ${formatMoney(
        chargeNow,
      )} when you submit this booking.`
    : isMixed
      ? `Your card will be charged ${formatMoney(
          chargeNow,
        )} now — full payment for ${formatServiceNames(
          paymentBuckets.fullNames,
        )} and a deposit for ${formatServiceNames(
          paymentBuckets.depositNames,
        )} — with the remaining ${formatMoney(dueLater)} due at your appointment.`
      : isFullCharge
        ? "Your card will be charged the full service price when you submit this booking."
        : "Your card will be charged this deposit when you submit this booking, with the remaining balance due at your appointment.";

  // `appointment` is typed as required but is briefly undefined at runtime:
  // closing the modal clears the form while this screen is still mounted, so
  // anything read during render must tolerate its absence.
  const bookingForm = appointment as AppointmentForm | undefined;
  // A charge of the whole total (or a mix with full-payment services) is a
  // "payment", never a "deposit" (Openings booking standard § 6).
  const policySentence = paymentPolicySentence(
    bookingForm?.cancellationPolicy,
    isFullCharge || isMixed ? "payment" : "deposit",
  );
  // The business this charge is collected for — the same resolution the
  // booking write path uses. Edition Studio is a separate business, so its
  // bookings must not be described as collected for Eddie's Edition.
  const businessName =
    BUSINESSES[
      (bookingForm && resolveBusinessForAppointment(bookingForm)) ??
        DEFAULT_EDITION_BUSINESS
    ].branding.displayName;

  const handleRouting = async (paymentMethodId: string) => {
    if (upcomingAppointments.length > 0) {
      onRouteChange(RESCHEDULE_MAIN, {
        phoneNumber,
        upcomingAppointments,
        client,
        paymentMethodId,
      });
      return;
    }

    setSubmitting(true);
    onCreate({
      firstName: customerName.firstName,
      lastName: customerName.lastName,
      phoneNumber,
      paymentMethodId,
      // createAppointment always verifies against the "booking:{salonId}"
      // purpose. Only a first-time caller's code (routeState.firstName is
      // only ever set on that path) was issued under that purpose; a
      // returning caller's verificationCode here comes from the
      // "rescheduleLookup:..." purpose and would fail with a purpose-mismatch
      // 404 if forwarded. Gate on firstName so this stays inert until a
      // first-time caller can reach this screen.
      ...(routeState.firstName &&
        routeState.verificationCode && { code: routeState.verificationCode }),
      errorCallback: (errorMessage) => {
        if (errorMessage) messageApi.error(errorMessage);
        setSubmitting(false);
      },
    });
  };

  const handleAddCard = async () => {
    // A retry after a FAILED booking must not take a second card. The Stripe
    // payment method and the CRM customer already exist from the first
    // attempt, nothing ever detaches them, and re-registering here left
    // several cards on the customer's account. Reuse what was created and go
    // straight back to the booking. "Use a different card" below is the way
    // out when the card itself was the problem.
    if (createdPaymentMethodId) {
      setSubmitting(true);
      handleRouting(createdPaymentMethodId);
      return;
    }

    if (stripe && elements) {
      setSubmitting(true);
      const res = await stripe.createPaymentMethod({
        elements,
      });
      if (res.error) {
        messageApi.error(extractErrorMessage(res.error.message));
        setSubmitting(false);
        return;
      } else {
        try {
          await bookingApi.createPaymentMethod(
            {
              firstName: customerName.firstName,
              lastName: customerName.lastName,
              phoneNumber: phoneNumber,
              paymentId: res.paymentMethod.id,
            },
            appointment,
          );
        } catch (e: any) {
          messageApi.error(extractErrorMessage(e));
          setSubmitting(false);
          return;
        }
        // createPaymentMethod succeeded: the Stripe payment method and CRM
        // customer now exist. Set this before handleRouting so it's captured
        // even if the appointment create below fails.
        setCreatedPaymentMethodId(res.paymentMethod.id);
      }
      handleRouting(res.paymentMethod.id);
    } else {
      messageApi.error("Can't find credit card information. Please try again.");
    }
  };

  return (
    <>
      {contextHolder}
      <Typography.Paragraph>
        This appointment requires payment to confirm.
      </Typography.Paragraph>

      <Space orientation="vertical" size={4} style={{ width: "100%" }}>
        {services.map((service, i) => (
          <Flex key={`svc-${i}`} justify="space-between" gap={12}>
            <Text>
              {service.title}
              {service.option ? ` (${service.option.title})` : ""}
            </Text>
            <Text>
              {/* With an option chosen the API charges option.price, so show
                  exactly that; a base-only starting-at price keeps its +. */}
              {service.option
                ? formatMoney(getServiceBasePrice(service))
                : (formatServicePrice(service) ??
                  formatMoney(getServiceBasePrice(service)))}
            </Text>
          </Flex>
        ))}
        <Divider style={{ margin: "8px 0" }} />
        <Flex justify="space-between" gap={12}>
          <Text strong>{chargeNowLabel}</Text>
          <Text strong>{formatMoney(chargeNow)}</Text>
        </Flex>
        {!isFullCharge && dueLater > 0 && (
          <Flex justify="space-between" gap={12}>
            <Text type="secondary">Remaining due at appointment</Text>
            <Text type="secondary">{formatMoney(dueLater)}</Text>
          </Flex>
        )}
      </Space>

      {/* The terms, then who collects the card payment: two paragraphs of
          the same small print, set tight so they read as one block. */}
      <Typography.Paragraph
        type="secondary"
        style={{ marginTop: 12, marginBottom: 4 }}
      >
        <small>
          {paymentMessage} {policySentence}
        </small>
      </Typography.Paragraph>
      <Typography.Paragraph
        type="secondary"
        data-testid="payment-collection-agent"
      >
        <small>{collectionAgentNotice(businessName)}</small>
      </Typography.Paragraph>

      <Flex align="start" gap={8} style={{ marginTop: 4 }}>
        <Checkbox
          id="appointment-policy-agreement"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          aria-labelledby="appointment-policy-label"
        />
        <Text id="appointment-policy-label">
          <label
            htmlFor="appointment-policy-agreement"
            style={{ cursor: "pointer" }}
          >
            I agree to the
          </label>{" "}
          <Link
            href="/terms-and-privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            appointment &amp; cancellation policy
          </Link>
          .
        </Text>
      </Flex>
      <Divider dashed />

      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        {createdPaymentMethodId ? (
          // The card is already registered, so the field is hidden and the
          // charge button reuses it. Without this escape hatch a DECLINED card
          // would be permanent: every retry, and every return through Back,
          // would reuse the card that just failed.
          <Flex justify="space-between" align="center" gap={12}>
            <Text type="secondary">Using the card you entered.</Text>
            <Button
              type="link"
              style={{ paddingRight: 0 }}
              disabled={isSubmitting}
              onClick={() => setCreatedPaymentMethodId(undefined)}
            >
              Use a different card
            </Button>
          </Flex>
        ) : (
          <div className="stripe-card-element-container">
            <CardElement />
          </div>
        )}
        <Flex justify="space-between" align="center">
          {routeState.firstName && (
            <Button
              type="link"
              style={{ paddingLeft: 0 }}
              disabled={isSubmitting}
              onClick={() =>
                onRouteChange(FIRST_TIME_CLIENT, {
                  phoneNumber,
                  firstName: routeState.firstName,
                  lastName: routeState.lastName,
                  verificationCode: routeState.verificationCode,
                  isCreditCardRequired: routeState.isCreditCardRequired,
                  verifyDuringAppointmentCreate:
                    routeState.verifyDuringAppointmentCreate,
                  ...(createdPaymentMethodId && {
                    paymentMethodId: createdPaymentMethodId,
                  }),
                })
              }
            >
              Back
            </Button>
          )}
          <Button
            loading={isSubmitting}
            disabled={!agreed}
            onClick={handleAddCard}
            type="primary"
          >
            {chargeLabel}
          </Button>
        </Flex>
      </Space>
    </>
  );
};

export default SetupPayment;
